import type { Chore, ChoreAssignment, Household, HouseholdAggregate } from './chores';

/**
 * What removing someone from a household changes, the same on the server (household-removeMember)
 * and on the owner's phone, which applies it at once rather than waiting for the next pull.
 * Their completions stay: the history of who did what isn't rewritten.
 */

/** The household's members without `memberId`, the display names kept parallel to the ids. */
export function withoutMember(
  household: Pick<Household, 'member_ids' | 'member_display_names'>,
  memberId: string,
): Pick<Household, 'member_ids' | 'member_display_names'> {
  const keep = household.member_ids.map((id) => id !== memberId);
  return {
    member_ids: household.member_ids.filter((_, i) => keep[i]),
    member_display_names: household.member_display_names.filter((_, i) => keep[i]),
  };
}

/**
 * The household's children with `memberId` made one or not, or null when that's already so. What
 * household-setChild writes, and what removing a member writes with `isChild` false.
 */
export function withChild(childIds: string[] | undefined, memberId: string, isChild: boolean): string[] | null {
  const ids = childIds ?? [];
  if (ids.includes(memberId) === isChild) return null;
  return isChild ? [...ids, memberId] : ids.filter((id) => id !== memberId);
}

/**
 * The household's members with `memberId` added under `name`, and among the children when they were
 * invited as one: what accepting an invitation writes (household-acceptHouseholdInvitation).
 */
export function withNewMember(
  household: Pick<Household, 'member_ids' | 'member_display_names' | 'child_ids'>,
  memberId: string,
  name: string,
  asChild: boolean,
): Pick<Household, 'member_ids' | 'member_display_names' | 'child_ids'> {
  const childIds = asChild ? withChild(household.child_ids, memberId, true) : null;
  return {
    member_ids: [...household.member_ids, memberId],
    member_display_names: [...household.member_display_names, name],
    ...(childIds && { child_ids: childIds }),
  };
}

/** A chore's assignments without `memberId`, or null when they weren't among them. */
export function assignmentsWithoutMember(assignments: ChoreAssignment[], memberId: string): ChoreAssignment[] | null {
  const kept = assignments.filter((a) => a.userId !== memberId);
  return kept.length === assignments.length ? null : kept;
}

/** Whether `userId` is one of the household's children (docs/plans/children.md). */
export function isChildIn(household: Pick<Household, 'child_ids'>, userId: string | undefined): boolean {
  return !!userId && (household.child_ids ?? []).includes(userId);
}

/** Whether `userId` is among the chore's assignees, primary or not. */
export function isAssignedTo(chore: Pick<Chore, 'assigned_to'>, userId: string): boolean {
  return chore.assigned_to.some((a) => a.userId === userId);
}

/**
 * The household as `userId` gets to see it: a child only the chores assigned to them, not even
 * ones with nobody assigned; anyone else all of them. The hiding is the app's, not the server's.
 */
export function householdAsSeenBy(aggregate: HouseholdAggregate, userId: string | undefined): HouseholdAggregate {
  if (!isChildIn(aggregate.household, userId)) return aggregate;
  return { ...aggregate, chores: aggregate.chores.filter((c) => isAssignedTo(c.chore, userId!)) };
}
